import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  JapaneseYen, Building2, User, Loader2, RefreshCw, Printer, Download,
  ChevronDown, ChevronUp, ArrowRight, Settings, Calendar, FileText, Trash2, Pencil, Save, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(n);

export default function BillingPage() {
  const [billings, setBillings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showRates, setShowRates] = useState(false);
  const [billingMonth, setBillingMonth] = useState(new Date().toISOString().slice(0, 7));
  const [serviceFilter, setServiceFilter] = useState('日中一時支援');

  useEffect(() => { fetchBillings(); }, [billingMonth]);

  const fetchBillings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/billings?billing_month=${billingMonth}`);
      setBillings(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/billings/generate?billing_month=${billingMonth}`);
      fetchBillings();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 409) {
        if (window.confirm(detail + '\n\n下書きを上書きして再生成しますか？')) {
          try {
            await axios.post(`${API}/billings/generate?billing_month=${billingMonth}&force=true`);
            fetchBillings();
          } catch (e2) { alert(e2.response?.data?.detail || '生成に失敗しました'); }
        }
      } else { alert(detail || '生成に失敗しました'); }
    } finally { setGenerating(false); }
  };

  const handleStatusUpdate = async (id, status) => {
    try { await axios.put(`${API}/billings/${id}/status?status=${status}`); fetchBillings(); }
    catch (e) { alert('更新に失敗しました'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('この請求データを削除しますか？')) return;
    try { await axios.delete(`${API}/billings/${id}`); fetchBillings(); }
    catch (e) { alert(e.response?.data?.detail || '削除に失敗しました'); }
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setEditForm({ total_units: b.total_units || 0, unit_price: b.unit_price || 10.37, user_burden: b.user_burden || 0, severity: b.severity || '軽度', notes: b.notes || '' });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/billings/${id}`, editForm);
      setEditingId(null);
      setDetailCache({});
      fetchBillings();
    } catch (e) { alert(e.response?.data?.detail || '更新に失敗しました'); }
  };

  const toggleDetail = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!detailCache[id]) {
      try {
        const res = await axios.get(`${API}/billings/${id}/detail`);
        setDetailCache(prev => ({ ...prev, [id]: res.data }));
      } catch (e) { console.error(e); }
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API}/billings/export-excel?billing_month=${billingMonth}&service_type=${encodeURIComponent(serviceFilter)}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${serviceFilter}_${billingMonth}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { alert('Excel出力に失敗しました'); }
  };

  const handleExportPdf = async () => {
    try {
      const res = await axios.get(`${API}/billings/export-pdf?billing_month=${billingMonth}&service_type=${encodeURIComponent(serviceFilter)}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${serviceFilter}_${billingMonth}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { alert('PDF出力に失敗しました'); }
  };

  const filteredBillings = billings.filter(b => b.service_type === serviceFilter);
  const totalAmount = filteredBillings.reduce((s, b) => s + (b.total_amount || 0), 0);
  const totalMunicipality = filteredBillings.reduce((s, b) => s + (b.municipality_burden || 0), 0);
  const totalUserBurden = filteredBillings.reduce((s, b) => s + (b.user_burden || 0), 0);

  const serviceTypes = [...new Set(billings.map(b => b.service_type))];
  if (!serviceTypes.includes('日中一時支援')) serviceTypes.unshift('日中一時支援');
  if (!serviceTypes.includes('移動支援')) serviceTypes.push('移動支援');

  const statusBadge = (s) => {
    const styles = { draft: 'bg-gray-100 text-gray-700', submitted: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-800', paid: 'bg-blue-100 text-blue-800' };
    const labels = { draft: '下書き', submitted: '提出済', approved: '承認済', paid: '支払済' };
    return <span className={`text-xs px-2 py-0.5 rounded-full ${styles[s] || styles.draft}`}>{labels[s] || s}</span>;
  };

  const renderBillingCard = (b, type) => {
    const isExpanded = expandedId === b.id;
    const detail = detailCache[b.id];
    const amount = type === 'municipality' ? (b.municipality_burden || 0) : (b.user_burden || 0);

    return (
      <Card key={`${type}-${b.id}`} className="card-surface overflow-hidden">
        <div className="p-3 cursor-pointer hover:bg-secondary/30 transition-colors" onClick={() => toggleDetail(b.id)} data-testid={`billing-card-${b.id}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">{b.client_name}</span>
              {statusBadge(b.status)}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg">{fmt(amount)}</span>
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>
          <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
            <span>{b.service_type}</span>
            <span>{b.total_units || 0}単位 x {b.unit_price || 10.37}円</span>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-border p-3 bg-secondary/10 space-y-3 text-sm">
            {editingId === b.id ? (
              /* 編集モード */
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div><label className="text-xs text-muted-foreground">障がい区分</label>
                    <select className="w-full border rounded p-1 text-sm" value={editForm.severity} onChange={e => setEditForm(p => ({ ...p, severity: e.target.value }))}>
                      <option value="軽度">軽度</option><option value="中度">中度</option><option value="重度">重度</option>
                    </select></div>
                  <div><label className="text-xs text-muted-foreground">合計単位数</label>
                    <input type="number" className="w-full border rounded p-1 text-sm" value={editForm.total_units} onChange={e => setEditForm(p => ({ ...p, total_units: parseInt(e.target.value) || 0 }))} /></div>
                  <div><label className="text-xs text-muted-foreground">単価（円/単位）</label>
                    <input type="number" step="0.01" className="w-full border rounded p-1 text-sm" value={editForm.unit_price} onChange={e => setEditForm(p => ({ ...p, unit_price: parseFloat(e.target.value) || 0 }))} /></div>
                  <div><label className="text-xs text-muted-foreground">利用者負担額</label>
                    <input type="number" className="w-full border rounded p-1 text-sm" value={editForm.user_burden} onChange={e => setEditForm(p => ({ ...p, user_burden: parseInt(e.target.value) || 0 }))} /></div>
                </div>
                <div><label className="text-xs text-muted-foreground">備考</label>
                  <input type="text" className="w-full border rounded p-1 text-sm" value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} placeholder="備考" /></div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => saveEdit(b.id)}><Save size={14} className="mr-1" />保存</Button>
                  <Button size="sm" variant="outline" onClick={cancelEdit}><X size={14} className="mr-1" />キャンセル</Button>
                </div>
              </div>
            ) : (
              /* 表示モード */
              <>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div><p className="text-xs text-muted-foreground">障がい区分</p><p className="font-bold">{b.severity || '軽度'}</p></div>
                  <div><p className="text-xs text-muted-foreground">請求総額</p><p className="font-bold">{fmt(b.total_amount || 0)}</p></div>
                  <div><p className="text-xs text-muted-foreground">市町村分</p><p className="font-bold text-blue-700">{fmt(b.municipality_burden || 0)}</p></div>
                  <div><p className="text-xs text-muted-foreground">利用者負担</p><p className="font-bold text-emerald-700">{fmt(b.user_burden || 0)}</p></div>
                  <div><p className="text-xs text-muted-foreground">利用日数</p><p>{b.service_days || 0}日</p></div>
                </div>

                {detail && detail.detail_rows && detail.detail_rows.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">日別明細（{detail.detail_rows.length}件）</p>
                    <div className="overflow-auto max-h-48">
                      <table className="w-full text-xs border-collapse">
                        <thead><tr className="bg-secondary text-left">
                          <th className="border p-1">日付</th><th className="border p-1">時間</th><th className="border p-1">分</th><th className="border p-1">区分</th><th className="border p-1">基本</th><th className="border p-1">加算</th><th className="border p-1">計</th>
                        </tr></thead>
                        <tbody>
                          {detail.detail_rows.map((r, i) => (
                            <tr key={i}><td className="border p-1">{r.date}</td><td className="border p-1">{r.start}-{r.end}</td><td className="border p-1">{r.minutes}</td><td className="border p-1">{r.category}</td><td className="border p-1">{r.base_units}</td><td className="border p-1">{r.addon_units}</td><td className="border p-1 font-bold">{r.day_units}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 no-print">
                  <Button size="sm" variant="outline" onClick={() => startEdit(b)} data-testid={`edit-billing-${b.id}`}><Pencil size={14} className="mr-1" />編集</Button>
                  {b.status === 'draft' && <Button size="sm" onClick={() => handleStatusUpdate(b.id, 'submitted')}>提出<ArrowRight size={14} className="ml-1" /></Button>}
                  {b.status === 'submitted' && <Button size="sm" onClick={() => handleStatusUpdate(b.id, 'approved')}>承認</Button>}
                  {b.status === 'approved' && <Button size="sm" onClick={() => handleStatusUpdate(b.id, 'paid')}>支払済</Button>}
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(b.id)} data-testid={`delete-billing-${b.id}`}><Trash2 size={14} className="mr-1" />削除</Button>
                </div>
              </>
            )}
          </div>
        )}
      </Card>
    );
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="billing-page">
      <style>{`@media print { .no-print { display: none !important; } }`}</style>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">請求管理</h1>
          <p className="text-muted-foreground mt-1">{billingMonth} {serviceFilter}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowRates(!showRates)} data-testid="toggle-rates"><Settings size={16} className="mr-1" />単価</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-button"><Printer size={16} className="mr-1" />印刷</Button>
          <Button variant="outline" onClick={handleExportExcel} data-testid="export-excel-button"><Download size={16} className="mr-1" />Excel</Button>
          <Button variant="outline" onClick={handleExportPdf} data-testid="export-pdf-button"><FileText size={16} className="mr-1" />PDF</Button>
          <Button className="btn-active" onClick={handleGenerate} disabled={generating} data-testid="generate-billing-button">
            {generating ? <Loader2 size={16} className="mr-1 animate-spin" /> : <RefreshCw size={16} className="mr-1" />}{generating ? '生成中' : '請求データ生成'}
          </Button>
        </div>
      </div>

      {showRates && (
        <Card className="card-surface no-print">
          <CardHeader><CardTitle className="text-base">津市日中一時支援 単位数テーブル（1単位=10.37円）</CardTitle></CardHeader>
          <CardContent>
            <table className="text-xs w-full border-collapse"><thead><tr className="bg-secondary"><th className="border p-1">区分</th><th className="border p-1">8h以上</th><th className="border p-1">6-8h</th><th className="border p-1">4-6h</th><th className="border p-1">2-4h</th></tr></thead>
              <tbody><tr><td className="border p-1">軽度</td><td className="border p-1">500</td><td className="border p-1">400</td><td className="border p-1">300</td><td className="border p-1">200</td></tr>
                <tr><td className="border p-1">中度</td><td className="border p-1">625</td><td className="border p-1">500</td><td className="border p-1">375</td><td className="border p-1">250</td></tr>
                <tr><td className="border p-1">重度</td><td className="border p-1">750</td><td className="border p-1">600</td><td className="border p-1">450</td><td className="border p-1">300</td></tr></tbody></table>
            <p className="text-xs text-muted-foreground mt-2">加算: 食事30, 入浴40, 送迎(者)27/(児)54, 夏季90</p>
          </CardContent>
        </Card>
      )}

      <div className="no-print flex flex-wrap gap-3 items-center">
        <Input type="month" value={billingMonth} onChange={e => setBillingMonth(e.target.value)} className="w-48" data-testid="filter-month-input" />
        <div className="flex gap-1 bg-secondary rounded-lg p-1">
          {serviceTypes.map(st => (
            <button key={st} onClick={() => setServiceFilter(st)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${serviceFilter === st ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`filter-service-${st}`}>
              {st}
              <span className="ml-1 text-xs">({billings.filter(b => b.service_type === st).length})</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Card className="border-2 border-blue-200 bg-blue-50/30">
            <CardContent className="p-4 flex items-center gap-3">
              <Building2 size={28} className="text-blue-600" />
              <div><p className="text-sm font-medium text-blue-800">市町村請求合計</p><p className="text-2xl font-bold text-blue-900">{fmt(totalMunicipality)}</p></div>
              <div className="ml-auto text-right"><p className="text-xs text-muted-foreground">{filteredBillings.length}件</p></div>
            </CardContent>
          </Card>
          {filteredBillings.map(b => renderBillingCard(b, 'municipality'))}
        </div>

        <div className="space-y-4">
          <Card className="border-2 border-emerald-200 bg-emerald-50/30">
            <CardContent className="p-4 flex items-center gap-3">
              <User size={28} className="text-emerald-600" />
              <div><p className="text-sm font-medium text-emerald-800">利用者負担合計</p><p className="text-2xl font-bold text-emerald-900">{fmt(totalUserBurden)}</p></div>
              <div className="ml-auto text-right"><p className="text-xs text-muted-foreground">{filteredBillings.filter(x => (x.user_burden || 0) > 0).length}件</p></div>
            </CardContent>
          </Card>
          {filteredBillings.map(b => renderBillingCard(b, 'user'))}
        </div>
      </div>

      {filteredBillings.length === 0 && (
        <Card className="card-surface"><CardContent className="py-12 text-center">
          <JapaneseYen size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-muted-foreground">請求データがありません</p>
          <p className="text-xs text-muted-foreground mt-1">「請求データ生成」で支援記録から自動生成できます</p>
        </CardContent></Card>
      )}
    </div>
  );
}
